import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Download, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubscriptions = ({ user, setUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthsFilter, setMonthsFilter] = useState("12");

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/admin/subscriptions/report`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { months: parseInt(monthsFilter, 10) },
        });
        setData(resp.data);
      } catch (err) {
        console.error("Subscription report error:", err);
        const msg = err.response?.data?.detail || "Error loading subscription report";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [monthsFilter]);

  const downloadCsv = () => {
    if (!data?.monthly_summaries) return;

    const rows = [
      ["Month", "6-Month Plans", "6-Month Revenue", "12-Month Plans", "12-Month Revenue", "Total Plans", "Total Revenue"],
      ...data.monthly_summaries.map((s) => [
        s.month,
        s.plan_6m_count,
        s.plan_6m_revenue.toFixed(2),
        s.plan_12m_count,
        s.plan_12m_revenue.toFixed(2),
        s.total_count,
        s.total_revenue.toFixed(2),
      ]),
    ];

    const csvContent = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `subscription_revenue_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="subscriptions" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
          <div>
            <h2
              className="text-2xl font-bold mb-1"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Subscription Revenue Report
            </h2>
            <p className="text-xs text-slate-500 max-w-xl">
              Monthly breakdown of subscription payments by plan type.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span>Period:</span>
              <select
                value={monthsFilter}
                onChange={(e) => setMonthsFilter(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 bg-white"
                data-testid="filter-months"
              >
                <option value="3">Last 3 months</option>
                <option value="6">Last 6 months</option>
                <option value="12">Last 12 months</option>
                <option value="24">Last 24 months</option>
              </select>
            </div>
            <Button
              onClick={downloadCsv}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              disabled={!data?.monthly_summaries?.length}
              data-testid="export-csv-btn"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Grand Total Banner */}
        {data && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <div className="text-sm text-green-800 font-medium">
                Total Subscription Revenue ({monthsFilter} months)
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-900 font-mono" data-testid="grand-total">
                ${(data.grand_total_revenue || 0).toFixed(2)} USD
              </div>
              <div className="text-xs text-green-700">
                {data.total_subscriptions || 0} subscription{(data.total_subscriptions || 0) !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center text-sm text-slate-600">
            Loading report...
            <BlueCubeSpinner />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600" data-testid="error-message">{error}</div>
        ) : !data?.monthly_summaries?.length ? (
          <div className="text-sm text-slate-600" data-testid="no-data">
            No subscription payments found in this period.
          </div>
        ) : (
          <>
            {/* Monthly Summary Table */}
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="subscriptions-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                      6-Month Plans
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                      6-Month Revenue
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">
                      12-Month Plans
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                      12-Month Revenue
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider bg-slate-100">
                      Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider bg-slate-100">
                      Total Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {data.monthly_summaries.map((summary) => (
                    <tr key={summary.month} data-testid={`row-${summary.month}`}>
                      <td className="px-6 py-3 font-medium text-slate-900">
                        {summary.month}
                      </td>
                      <td className="px-6 py-3 text-center text-slate-700">
                        {summary.plan_6m_count}
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-slate-700">
                        ${summary.plan_6m_revenue.toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-center text-slate-700">
                        {summary.plan_12m_count}
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-slate-700">
                        ${summary.plan_12m_revenue.toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-center font-semibold text-slate-900 bg-slate-50">
                        {summary.total_count}
                      </td>
                      <td className="px-6 py-3 text-right font-mono font-semibold text-slate-900 bg-slate-50">
                        ${summary.total_revenue.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Plan Summary Cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-sm font-medium text-slate-600 mb-2">6-Month Plans</div>
                <div className="flex justify-between items-baseline">
                  <div className="text-2xl font-bold text-slate-900">
                    {data.monthly_summaries.reduce((acc, s) => acc + s.plan_6m_count, 0)}
                  </div>
                  <div className="text-lg font-mono text-slate-700">
                    ${data.monthly_summaries.reduce((acc, s) => acc + s.plan_6m_revenue, 0).toFixed(2)}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1">subscriptions @ $20 each</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-sm font-medium text-slate-600 mb-2">12-Month Plans</div>
                <div className="flex justify-between items-baseline">
                  <div className="text-2xl font-bold text-slate-900">
                    {data.monthly_summaries.reduce((acc, s) => acc + s.plan_12m_count, 0)}
                  </div>
                  <div className="text-lg font-mono text-slate-700">
                    ${data.monthly_summaries.reduce((acc, s) => acc + s.plan_12m_revenue, 0).toFixed(2)}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1">subscriptions @ $30 each</div>
              </div>
            </div>

            {/* Revenue by Organization */}
            {data.organization_summaries && data.organization_summaries.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Revenue by Organization</h3>
                <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">
                          Organization
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                          6-Month
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                          12-Month
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-600 uppercase">
                          Total
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600 uppercase">
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {data.organization_summaries.map((org) => (
                        <tr key={org.organization}>
                          <td className="px-4 py-2 font-medium text-slate-900">
                            {org.organization}
                          </td>
                          <td className="px-4 py-2 text-center text-slate-600">
                            {org.plan_6m_count}
                          </td>
                          <td className="px-4 py-2 text-center text-slate-600">
                            {org.plan_12m_count}
                          </td>
                          <td className="px-4 py-2 text-center font-semibold text-slate-900">
                            {org.total_count}
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">
                            ${org.total_revenue.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptions;
